import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import { TableHeadComponent } from "../../common/TableHead";
import React from "react";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import DeleteIcon from "@mui/icons-material/Delete";
import useWindowDimensions from "../../common/useWindowDimensions";
import { DialogActions, TextField } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers";
import { yyyymmddGenerator } from "../Analytics/AnalyticsUtils/yyyymmddGenerator";
import ErrorBoundary from "../../../ErrorBoundary";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { AddEventDialog } from "./AddEventDialog";

type CalendarEvent = {
  event_date: string;
  name: string;
  start_time?: string | null;
  end_time?: string | null;
  editMode?: boolean;
};

export const StepTwoAddCalendar = ({
  calendarEvents,
  setCalendarEvents,
}: {
  calendarEvents: CalendarEvent[];
  setCalendarEvents: React.Dispatch<React.SetStateAction<CalendarEvent[]>>;
}) => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const getMonthName = (monthNumber: number) => {
    return monthNames[monthNumber - 1];
  };

  const formatDate = (date: string) => {
    if (date === "--") {
      return "--";
    }
    const parts = date.split("-");
    return `${getMonthName(Number(parts[1]))} ${parts[2]}, ${parts[0]}`;
  };

  const { width, height } = useWindowDimensions();

  const [calcuatedHeight, setCalculatedHeight] = React.useState(0);

  React.useEffect(() => {
    setCalculatedHeight(height - 225);
  }, [height, width]);

  const [calendarDate, setCalendarDate] = React.useState(new Date());

  const [tempString, setTempString] = React.useState("");

  const [addDateDisabled, setAddDateDisabled] = React.useState(false);

  const [currentCalendarEvents, setCurrentCalendarEvents] =
    React.useState(calendarEvents);
  const [addEventDialogOpen, setAddEventDialogOpen] = React.useState(false);
  const { isFeatureEnabled } = useFeatureFlag();
  const showAddCalendarScheduleTime = isFeatureEnabled(
    "showAddCalendarScheduleTime",
    false
  );

  React.useEffect(() => {
    setCurrentCalendarEvents(calendarEvents);
  }, [calendarEvents]);

  const handleDeleteEvent = (rowIndex: number) => {
    const nextEvents = currentCalendarEvents.filter(
      (_event: CalendarEvent, index: number) => index !== rowIndex
    );
    setCurrentCalendarEvents(nextEvents);
    setCalendarEvents(nextEvents);
  };

  const handleSaveLegacyInlineEvent = (rowIndex: number) => {
    const nextEvents = [...currentCalendarEvents];
    nextEvents[rowIndex] = {
      ...nextEvents[rowIndex],
      editMode: false,
      name: tempString,
      event_date: yyyymmddGenerator(calendarDate),
    };
    setCurrentCalendarEvents(nextEvents);
    setCalendarEvents(nextEvents);
    setAddDateDisabled(false);
  };

  const handleAddLegacyInlineEvent = () => {
    const nextEvents = [
      ...currentCalendarEvents,
      {
        event_date: yyyymmddGenerator(new Date()),
        name: "",
        editMode: true,
      },
    ];
    setCurrentCalendarEvents(nextEvents);
    setTempString("");
    setAddDateDisabled(true);
    setCalendarDate(new Date());
  };

  const handleDialogSubmit = ({
    event_date,
    name,
    start_time,
    end_time,
  }: {
    event_date: string;
    name: string;
    start_time: string | null;
    end_time: string | null;
  }) => {
    const nextEvents = [
      ...currentCalendarEvents,
      { event_date, name, start_time, end_time },
    ];
    setCurrentCalendarEvents(nextEvents);
    setCalendarEvents(nextEvents);
    setAddEventDialogOpen(false);
  };

  return (
    <ErrorBoundary>
      <div>
        <div className="absolute top-[160px] left-[30px]">
          <TableContainer sx={{ maxHeight: calcuatedHeight, width: 540 }}>
            <Table
              sx={{ fontFamily: "Mulish", width: "100%", overflow: "scroll" }}
              aria-label="simple table"
              stickyHeader
            >
              <TableHeadComponent
                titlesArray={["Event Date", "Event Name", ""]}
              />
              <TableBody>
                {currentCalendarEvents.map(
                  (object: CalendarEvent, rowIndex: number) => {
                  if (object.editMode) {
                    return (
                      <TableRow key={`inline-edit-row-${rowIndex}`}>
                        <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              value={calendarDate}
                              onChange={(newValue: Date | null) => {
                                if (!newValue) {
                                  return;
                                }
                                setCalendarDate(newValue);
                                const nextEvents = [...currentCalendarEvents];
                                nextEvents[rowIndex] = {
                                  ...nextEvents[rowIndex],
                                  event_date: yyyymmddGenerator(newValue),
                                };
                                setCurrentCalendarEvents(nextEvents);
                                setCalendarEvents(nextEvents);
                              }}
                              renderInput={(params) => (
                                <TextField sx={{ width: 150 }} {...params} />
                              )}
                            />
                          </LocalizationProvider>
                        </TableCell>
                        <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                          <TextField
                            value={tempString}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setTempString(event.target.value);
                            }}
                            variant="standard"
                            sx={{ fontFamily: "Mulish", width: 200 }}
                          />
                        </TableCell>
                        <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                          <OrangeTextButton
                            onClick={() => handleSaveLegacyInlineEvent(rowIndex)}
                            disabled={false}
                            text="Save"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  } else
                    return (
                      <TableRow key={`event-row-${object.name}-${rowIndex}`}>
                        <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                          {formatDate(object.event_date)}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{ fontFamily: "Mulish", width: 250 }}
                        >
                          {object.name}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontFamily: "Mulish", width: 100 }}
                        >
                          <OrangeTextButton
                            onClick={() => handleDeleteEvent(rowIndex)}
                            text={<DeleteIcon />}
                            disabled={false}
                          />
                        </TableCell>
                      </TableRow>
                    );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <DialogActions>
            <span>
              <OrangeTextButton
                onClick={() => {
                  if (showAddCalendarScheduleTime) {
                    setAddEventDialogOpen(true);
                    return;
                  }
                  handleAddLegacyInlineEvent();
                }}
                text="+ Add Event"
                disabled={showAddCalendarScheduleTime ? false : addDateDisabled}
              />
            </span>
          </DialogActions>
        </div>
        <AddEventDialog
          open={addEventDialogOpen}
          onCancel={() => setAddEventDialogOpen(false)}
          onSubmit={handleDialogSubmit}
        />
      </div>
    </ErrorBoundary>
  );
};
